import { useApiInstance } from '@api-hooks/ApiInstanceContext';
import { useGetOptions } from '@api-hooks/useOptions';
import { updateMyPasswordByToken } from '@api/management/user/User';
import Button from '@components/Button/Button';
import { useGlobalModal } from '@components/GlobalModal';
import InputGroup from '@components/InputGroup/InputGroup';
import Modal from '@components/Modal/Modal';
import ProgressIcon from '@components/base/ProgressIcon';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { MANAGE_OPTION_CATEGORY, SessionInfo } from 'common-types';
import { t as i18n } from 'i18next';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const variants = {
  label: 'mb-2px text-body1',
};

const schema = z
  .object({
    currentPassword: z
      .string({
        required_error: i18n('enterYourPassword'),
      })
      .trim()
      .min(1, {
        message: i18n('enterYourPassword'),
      }),
    newPassword: z
      .string()
      .trim()
      .min(1, {
        message: i18n('enterYourPassword'),
      }),
    confirmNewPassword: z
      .string()
      .trim()
      .min(1, {
        message: i18n('enterYourPassword'),
      }),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: i18n('passwordNotMatch'),
    path: ['confirmNewPassword'],
  });

type Schema = z.infer<typeof schema>;

/**
 * 로그인 > 비밀번호 변경한지 [days]가 지났는지 확인 > 비밀번호 변경 모달
 * @param props
 * @returns
 */

export default function PasswordChangeModal({
  open,
  setOpen,
  loginResult,
  signIn,
  redirectUrl,
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
  loginResult?: SessionInfo | null;
  signIn: (SessionInfo?: SessionInfo | null) => void;
  redirectUrl: string;
}) {
  const apiInstance = useApiInstance();

  const router = useRouter();
  const { t } = useTranslation();

  const {
    handleSubmit,
    register,
    setError,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<Schema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
  });
  const { setSimpleModal, setErrorModal, errorModal } = useGlobalModal();

  const { data: passwordPolicy, isLoading: isLoadingPasswordPolicy } = useGetOptions(
    apiInstance,
    MANAGE_OPTION_CATEGORY.PASSWORD_POLICY,
    open,
    {
      'X-API-KEY': loginResult?.token,
    }
  );

  const passwordRegExp = useMemo(
    () => new RegExp(`${passwordPolicy?.constraint || '.*'}`),
    [passwordPolicy?.constraint]
  );

  const { mutate, isPending } = useMutation({
    mutationKey: ['updateMyPassword', loginResult?.token],
    mutationFn: (data: Schema) =>
      updateMyPasswordByToken(apiInstance, {
        token: loginResult?.token as string,
        myPassword: data.currentPassword,
        updatePassword: data.newPassword,
      }),
    onSuccess: () => {
      setSimpleModal({
        open: true,
        text: t('passwordChanged'),
        successCallback: () => {
          signIn?.(loginResult);
        },
      });
    },
    onError: (err: any) => {
      setErrorModal({
        open: true,
        text: err?.translatedErrorMessage || t('currentPasswordIncorrect'),
      });
    },
  });

  async function onSubmit(data: Schema) {
    if (errorModal.open) return;
    if (passwordRegExp && !passwordRegExp.test(data.newPassword)) {
      setError('newPassword', { message: t('passwordConstraintsError') });
      return;
    }
    if (passwordRegExp && !passwordRegExp.test(data.confirmNewPassword)) {
      setError('confirmNewPassword', { message: t('passwordConstraintsError') });
      return;
    }
    mutate(data);
  }
  const close = useCallback(() => setOpen(false), [setOpen]);

  const canChangeLater =
    !isLoadingPasswordPolicy && passwordPolicy?.forceUpdatePasswordAfterRetention === false;

  return (
    <>
      <Modal open={open} onClose={() => {}}>
        <Modal.Title>
          <h2>{t('changePassword')}</h2>
          {canChangeLater && <Modal.Close onClick={close} />}
        </Modal.Title>
        <Modal.Body className="min-w-[400px]">
          <form
            id="password-change-form"
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col"
          >
            <InputGroup
              type="password"
              className="h-90px"
              {...register('currentPassword')}
              id="currentPassword"
              autoComplete="current-password"
              size="lg"
              label={t('currentPassword')}
              error={errors?.currentPassword}
              placeholder={t('placeholderPassword')}
              labelClassName={variants.label}
              required
            />
            <InputGroup
              type="password"
              className="h-90px"
              {...register('newPassword')}
              id="newPassword"
              size="lg"
              label={t('newPassword')}
              error={errors?.newPassword}
              placeholder={t('newPassword')}
              labelClassName={variants.label}
              required
            />
            <InputGroup
              type="password"
              className="h-90px"
              {...register('confirmNewPassword')}
              id="confirmNewPassword"
              size="lg"
              label={t('confirmNewPassword')}
              error={errors?.confirmNewPassword}
              placeholder={t('confirmNewPassword')}
              labelClassName={variants.label}
              required
            />
            <input type="submit" hidden />
          </form>
          <div className="flex justify-center items-center w-full gap-12px mt-30px">
            {canChangeLater && (
              <Button
                onClick={() => {
                  router.replace(redirectUrl);
                  close();
                }}
              >
                {t('changePasswordLater')}
              </Button>
            )}
            <Button
              color="active"
              type="submit"
              form="password-change-form"
              disabled={isPending || Object.keys(errors).length > 0}
            >
              <div className="flex flex-row justify-center items-center space-x-4">
                <div>{t('change')}</div>
                {isPending ? <ProgressIcon size={5} /> : null}
              </div>
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
